import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { TFunction, useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { MultipleActionsParams } from "go-list/list/services/types";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ProductType } from "../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../services/Api/api";
import { SearchSelectApi } from "../../../../../../../services/Api/types";
import { ModalMultipleActionType } from "../services/types";
import { measureTypeOptions } from "../utils";

interface Props {
	show: boolean;
	onHide: () => void;
	productIds: string;
	handleSave: () => void;
	allItemsSelected: boolean;
	multipleActionParams?: MultipleActionsParams;
	multipleActionType: ModalMultipleActionType;
	productType: ProductType;
}

interface ProductMultipleActionConfig {
	formSelectLabel: string;
	formSelectName: string;
	formSelectOptions: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	) => Promise<SearchSelectApi[]> | SearchSelectApi[];
}

const getMultipleActionConfig = (
	multipleActionType: ModalMultipleActionType,
	t: TFunction
): ProductMultipleActionConfig | undefined => {
	switch (multipleActionType) {
		case ModalMultipleActionType.ASSIGN_CATEGORY:
			return {
				formSelectLabel: t(`common.word.${multipleActionType}`),
				formSelectOptions: (search: string, params: Record<string, any>, options?: Record<string, any>) => {
					return api.organization().getProductCategorySearchSelect(search, params, {
						cancelToken: options?.token,
					});
				},
				formSelectName: `product_${multipleActionType}_id`,
			};
		case ModalMultipleActionType.ASSIGN_TAX:
			return {
				formSelectLabel: t(`common.word.${multipleActionType}`),
				formSelectOptions: (search: string, params: Record<string, any>, options?: Record<string, any>) => {
					return api.organization().getTaxSearchSelect(search, params, {
						cancelToken: options?.token,
					});
				},
				formSelectName: `${multipleActionType}_id`,
			};
		case ModalMultipleActionType.ASSIGN_MEASURE_TYPE:
			return {
				formSelectLabel: t(`common.word.${multipleActionType}`),
				formSelectOptions: () =>
					measureTypeOptions.map((measureTypeOption) => ({
						label: t(`enums.products.measure_types.${measureTypeOption}`),
						id: measureTypeOption,
					})),
				formSelectName: multipleActionType,
			};
		default:
			return undefined;
	}
};

export const ProductMultipleActionsModal = ({
	show,
	onHide,
	productIds,
	handleSave,
	allItemsSelected,
	multipleActionParams,
	multipleActionType,
	productType,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [customFields, setCustomFields] = useState<CustomFieldTemplateApi[] | undefined>();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<Record<string, any>>({
		criteriaMode: "all",
	});
	const { handleSubmit, formState, control, setError } = form;
	const { formSelectLabel, formSelectOptions, formSelectName } = getMultipleActionConfig(multipleActionType, t) ?? {};
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const customFieldsParams = useMemo(
		() => ({
			f: btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}resource_type|e=PRODUCT`))),
			include: "options,resources,permissions,constraints",
		}),
		[]
	);

	const onSubmit = handleSubmit(async (data: Record<string, any>) => {
		if (!validateCustomErrors()) return;
		setLoading(true);
		try {
			const params: Record<string, any> = multipleActionParams ? { ...multipleActionParams } : {};
			const decodedFilterParam = decodeURIComponent(escape(atob(params.f)));
			const prefix = decodedFilterParam.startsWith(NEW_WAY_TO_ENCODING_FILTER_SIGN)
				? ""
				: NEW_WAY_TO_ENCODING_FILTER_SIGN;
			const filterParamIncludingProductTypeFilter = `${prefix}${decodedFilterParam}${FILTER_SEPARATOR}product_type|e=${productType}`;
			params.f = btoa(unescape(encodeURIComponent(filterParamIncludingProductTypeFilter)));
			if (!allItemsSelected) params.id = productIds;

			await api.organization().patchProducts(data, params);
			addSuccessFlash(t("lib:common.flash.saved"));
			onHide();
			handleSave();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const onFetchCustomFields = useCallback(async () => {
		try {
			const res = await api.organization().getCustomFields(customFieldsParams);
			setCustomFields(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	}, [addFlash, customFieldsParams]);

	useEffect(() => {
		if (multipleActionType !== ModalMultipleActionType.ASSIGN_CUSTOM_FIELDS) return;
		onFetchCustomFields();
	}, [multipleActionType, onFetchCustomFields]);

	return (
		<Modal show={show} onHide={onHide}>
			<FormDirty formState={formState}>
				<Modal.Header closeButton>
					<Modal.Title>{t(`modules.product.action.assign_${multipleActionType}.title`)}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{multipleActionType === ModalMultipleActionType.ASSIGN_CUSTOM_FIELDS ? (
						<CustomFieldsForm
							form={form}
							customFields={[]}
							customFieldsConfig={customFields}
							setErrors={setErrors}
						/>
					) : (
						<FormSelectGroup
							errors={[]}
							label={formSelectLabel}
							control={control}
							name={formSelectName}
							loadOptions={formSelectOptions}
							getOptionValue={(option) => option.id}
							data-testid={formSelectName}
						/>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.save")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};
